import { computed, ref } from "vue"
import { acceptHMRUpdate, defineStore } from "pinia"
import i18n from "@/locales"

import userService from "@/services/user.service"
import assetsService from "@/services/assets.service"
import useToastManager from "@/composables/toastManager"
import { localStorageService } from "@/services/localStorage.service"
import { useAuthentication } from "@/composables/useAuthentication"
import { setI18nLocale } from "../locales"

export const useUserStore = defineStore("user", () => {
  const user = ref(null)
  const userAvatar = computed(() => user.value?.avatarUrl)
  const userAvatarLetter = computed(() =>
    user.value?.name?.length ? user.value?.name.split("")[0] : ""
  )

  const userIsLoggedIn = computed(() => Boolean(localStorageService.getUserUuid()))

  async function updateUser(payload = {}, options) {
    await userService.update(user.value?.iri, { payload })

    if (options?.notification) useToastManager().success(options?.notification)

    if (options?.setUser) setUser({ force: true })
    else {
      Object.assign(user.value, payload)
    }
  }

  async function changeAvatar(file) {
    await userService.updateAvatar(user.value?.iri, file, user.value?.locale)
    setUser({ force: true })
  }

  async function setUser(options = { force: false }) {
    if (user.value && !options.force) return
    try {
      const _user = await userService.getItem(localStorageService.getUserUuid())
      const _userAvatarExists = await assetsService.getItem(_user.avatar?.uuid)

      if (_user) {
        user.value = { ..._user, avatar: _userAvatarExists ? _user.avatar : null }
        setI18nLocale(user.value.locale)
      }
    } catch (error) {
      console.error(`Error setUser ${error}`)
    }
  }

  async function loadUser(options = { force: false }) {
    if (localStorageService.getUserUuid()) {
      await setUser(options)
    }
  }

  async function changePassword({ currentPassword, newPassword }) {
    try {
      await userService.updatePassword(user.value?.uuid, { currentPassword, password: newPassword })
      useToastManager().success({
        message: i18n.global.t("component.notification.password.success.body")
      })

      return Promise.resolve()
    } catch (error) {
      if (!error.violations) {
        useToastManager().error({
          message: i18n.global.t("component.alert.contact.support.label")
        })
      }

      return Promise.reject(error)
    }
  }

  async function createPassword({ newPassword }) {
    try {
      const _userUpdated = await userService.createPassword(user.value?.uuid, newPassword)
      user.value = _userUpdated
      useToastManager().success({
        message: i18n.global.t("component.notification.password.success.body")
      })

      return Promise.resolve()
    } catch (error) {
      if (!error.violations) {
        useToastManager().error({
          message: i18n.global.t("component.alert.contact.support.label")
        })
      }

      return Promise.reject(error)
    }
  }

  async function resetPassword(uuid, payload) {
    try {
      const response = await userService.resetPassword(uuid, payload)
      useAuthentication().authSuccessHandler(response)
    } catch (error) {
      return Promise.reject(error)
    }
  }

  function resetState() {
    user.value = null
  }

  return {
    user,
    userAvatar,
    userIsLoggedIn,
    userAvatarLetter,
    setUser,
    loadUser,
    changeAvatar,
    changePassword,
    createPassword,
    resetPassword,
    updateUser,
    resetState
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
